import QRCode from 'qrcode';
import { api } from '../api.js';
import { auth } from '../auth.js';
import { router } from '../router.js';
import { escapeHtml, showToast } from '../utils.js';
import { formatRange, toLocalInputValue, openModal } from './events.js';
import { refresh as refreshEventMenu } from '../events-nav.js';

let state = { event: null, tab: 'overview', media: [], guestbook: [], guests: [] };

/**
 * Uploads are type-checked and scanned for malware before they appear, so the Photos tab
 * refreshes itself while anything is still being checked, rather than asking the organiser to
 * reload. One timer, cleared on every redraw -- the router has no teardown hook.
 */
const MEDIA_REFRESH_MS = 5000;
const MEDIA_REFRESH_LIMIT = 36;
let mediaRefreshTimer = null;

/** A background goes live only once it is checked; the page waits up to three minutes for that. */
const BACKGROUND_POLL_MS = 2500;
const BACKGROUND_POLL_LIMIT = 72;

/** Matches STRIKES_BEFORE_BLOCK in api/src/repositories/detection-repository.ts. */
const STRIKES_BEFORE_BLOCK = 3;

const KIND_LABELS = {
    photo: 'Photo',
    video: 'Video',
    poster: 'Video poster',
    audio: 'Voice message',
    background: 'Background image',
};

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
const isChecking = (status) => status === 'pending' || status === 'checking';

export async function renderEventAdmin(params) {
    const app = document.getElementById('app');
    const id = Number(params?.id);
    if (!Number.isInteger(id)) return renderMissing(app);

    app.innerHTML = `<div class="container"><p class="text-muted">Loading event…</p></div>`;

    try {
        state = { event: await api.getEvent(id), tab: 'overview', media: [], guestbook: [], guests: [] };
    } catch (err) {
        return renderMissing(app, err.message);
    }

    draw();
}

function renderMissing(app, detail) {
    app.innerHTML = `
        <div class="container">
            <div class="empty-state">
                <i class="fa-solid fa-calendar-xmark"></i>
                <h2>Event not found</h2>
                <p>${escapeHtml(detail ?? "That event doesn't exist, or isn't yours.")}</p>
                <a href="/events" data-link class="btn btn-primary">Back to events</a>
            </div>
        </div>`;
}

function guestUrl(slug) {
    return `${window.location.origin}/e/${slug}`;
}

function draw() {
    clearTimeout(mediaRefreshTimer);
    const ev = state.event;
    const starts = new Date(ev.starts_at);
    const ends = new Date(ev.ends_at);

    document.getElementById('app').innerHTML = `
        <div class="container">
            <a href="/events" data-link class="event-back"><i class="fa-solid fa-arrow-left"></i> All events</a>

            <div class="page-header">
                <div>
                    <h1>${escapeHtml(ev.name)}</h1>
                    <p class="event-card-when">${escapeHtml(formatRange(starts, ends))}</p>
                </div>
                <div class="event-header-actions">
                    ${ev.is_active ? '<span class="event-badge event-badge-live">Live now</span>' : ''}
                    <button type="button" id="edit-event" class="btn btn-secondary">
                        <i class="fa-solid fa-pen"></i> Edit
                    </button>
                    ${ev.is_owner ? `<button type="button" id="delete-event" class="btn btn-danger">
                        <i class="fa-solid fa-trash"></i> Delete
                    </button>` : ''}
                </div>
            </div>

            <nav class="event-tabs">
                ${tabButton('overview', 'fa-qrcode', 'Invite')}
                ${tabButton('media', 'fa-images', 'Photos &amp; video')}
                ${tabButton('guestbook', 'fa-pen-nib', 'Guest book')}
                ${tabButton('guests', 'fa-user-group', 'Guests')}
            </nav>

            <div id="event-tab-body"></div>
        </div>`;

    document.getElementById('edit-event').addEventListener('click', openEditModal);
    document.getElementById('delete-event')?.addEventListener('click', confirmDelete);

    for (const btn of document.querySelectorAll('[data-tab]')) {
        btn.addEventListener('click', () => {
            state.tab = btn.dataset.tab;
            draw();
        });
    }

    drawTab();
}

function tabButton(key, icon, label) {
    return `<button type="button" data-tab="${key}" class="event-tab${state.tab === key ? ' active' : ''}">
        <i class="fa-solid ${icon}"></i> ${label}
    </button>`;
}

async function drawTab() {
    const body = document.getElementById('event-tab-body');
    if (state.tab === 'overview') return drawOverview(body);
    if (state.tab === 'media') return drawMedia(body);
    if (state.tab === 'guestbook') return drawGuestbook(body);
    return drawGuests(body);
}

// ------------------------------------------------------------------ Invite / QR

function drawOverview(body) {
    const ev = state.event;
    const url = guestUrl(ev.slug);

    body.innerHTML = `
        <div class="event-invite">
            <div class="event-invite-qr">
                <canvas id="event-qr" width="260" height="260"></canvas>
                <div class="event-invite-buttons">
                    <button type="button" id="download-qr" class="btn btn-secondary btn-sm">
                        <i class="fa-solid fa-download"></i> QR code
                    </button>
                    <button type="button" id="download-card" class="btn btn-primary btn-sm">
                        <i class="fa-solid fa-print"></i> Printable card
                    </button>
                </div>
            </div>
            <div class="event-invite-detail">
                <h2>How guests join</h2>
                <ol class="event-steps">
                    <li>They scan this code, or open the link below.</li>
                    <li>They give a name and email address, and confirm a six-digit code.</li>
                    <li>They can add photos, video and guest-book messages until the event ends.</li>
                </ol>
                <label class="event-link-label" for="event-link">Link</label>
                <div class="event-link-row">
                    <input type="text" id="event-link" readonly value="${escapeHtml(url)}">
                    <button type="button" id="copy-link" class="btn btn-secondary btn-sm">Copy</button>
                </div>
                <p class="form-hint">
                    Anyone with this link can ask to join, so treat it like the invitation itself.
                    They see nothing but the name and date until they confirm an email address.
                </p>

                <div class="event-background-panel">
                    <h2 class="event-subheading">Background image</h2>
                    <p class="form-hint">
                        Fills the guest page behind everything, cropped to fit rather than
                        stretched. A wide, uncluttered picture works best &mdash; text sits on top of it.
                    </p>
                    <div id="background-preview-slot"></div>
                    <div class="event-background-buttons">
                        <label class="btn btn-secondary btn-sm">
                            <i class="fa-solid fa-image"></i> Choose image
                            <input type="file" id="background-file" accept="image/*" hidden>
                        </label>
                        <button type="button" id="remove-background" class="btn btn-secondary btn-sm${ev.background_url ? '' : ' hidden'}">
                            Remove
                        </button>
                    </div>
                    <p id="background-status" class="form-hint">${ev.background_checking
                        ? 'A new background is being checked. It will replace this one when it clears.'
                        : ''}</p>
                </div>

                <h2 class="event-subheading">Who can manage this event</h2>
                <p id="admin-summary" class="text-muted">Loading…</p>
                ${ev.is_owner && !auth.isLimited() ? `<button type="button" id="edit-admins" class="btn btn-secondary btn-sm">
                    <i class="fa-solid fa-user-gear"></i> Change
                </button>` : '<p class="form-hint">Only the owner can change this.</p>'}
            </div>
        </div>`;

    const canvas = document.getElementById('event-qr');
    QRCode.toCanvas(canvas, url, { width: 260, margin: 1 }).catch(() => {
        body.querySelector('.event-invite-qr').insertAdjacentHTML('afterbegin',
            '<p class="form-error">Could not draw the QR code.</p>');
    });

    document.getElementById('copy-link').addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(url);
            showToast('Link copied', 'success');
        } catch {
            // Clipboard access is refused outside a secure context and in some browsers;
            // selecting the text is a working fallback rather than a dead button.
            document.getElementById('event-link').select();
            showToast('Press Ctrl+C to copy', 'info');
        }
    });

    document.getElementById('download-qr').addEventListener('click', () => {
        downloadDataUrl(canvas.toDataURL('image/png'), `${slugifyName(state.event.name)}-qr.png`);
    });
    document.getElementById('download-card').addEventListener('click', () => downloadCard(url));

    renderBackgroundPreview();
    document.getElementById('background-file').addEventListener('change', (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (file) void uploadBackground(file);
    });
    document.getElementById('remove-background').addEventListener('click', removeBackground);

    document.getElementById('edit-admins')?.addEventListener('click', openAdminsModal);
    void renderAdminSummary();
}

async function renderAdminSummary() {
    const el = document.getElementById('admin-summary');
    if (!el) return;
    // A limited account cannot read the family list, nor add anyone from it.
    if (auth.isLimited()) {
        el.textContent = 'Just you. Joint admins are not available on this account.';
        return;
    }
    try {
        const { users } = await api.listShareTargets();
        const names = state.event.admin_ids
            .map((id) => users.find((u) => u.id === id)?.full_name)
            .filter(Boolean);
        el.textContent = names.length
            ? `You, plus ${names.join(', ')}.`
            : 'Just you. Add someone else to help moderate.';
    } catch {
        el.textContent = 'Could not load the admin list.';
    }
}

function renderBackgroundPreview() {
    const slot = document.getElementById('background-preview-slot');
    if (!slot) return;
    slot.innerHTML = state.event.background_url
        ? `<img class="event-background-preview" src="${escapeHtml(state.event.background_url)}" alt="Event background">`
        : `<div class="event-background-empty"><i class="fa-solid fa-image"></i></div>`;
    document.getElementById('remove-background')?.classList.toggle('hidden', !state.event.background_url);
}

/**
 * Re-encodes in the browser before uploading.
 *
 * A background is one image seen by every guest on whatever signal the venue has, so
 * sending a 5MB phone original would be the slowest thing in the whole feature. Nothing on
 * the server can resize it -- the derivatives pipeline is wired to `originals/` and would
 * also file the result in the owner's gallery, which a page background has no business
 * being in. So it happens here, the same trick the guest page uses for video posters.
 */
async function downscaleToWebp(file, maxEdge = 2560) {
    let bitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        // Gallery photos are converted on the server, but a background is converted here, and
        // only Safari can open HEIC in a canvas.
        throw new Error(/\.hei[cf]$/i.test(file.name) || /hei[cf]/i.test(file.type)
            ? "This browser can't open HEIC images. Choose a JPEG or PNG for the background."
            : 'This browser could not open that image.');
    }
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
    if (!blob) throw new Error('This browser could not process that image');
    return blob;
}

async function uploadBackground(file) {
    const status = document.getElementById('background-status');
    const setStatus = (text) => { if (status) status.textContent = text; };
    setStatus('Processing…');

    try {
        const blob = await downscaleToWebp(file);
        setStatus('Uploading…');

        const { upload, background_id: uploadedId } = await api.createEventBackgroundUpload(state.event.id);
        await api.uploadToS3(upload, blob, (pct) => {
            setStatus(`Uploading… ${Math.round(pct * 100)}%`);
        });

        // Like every upload, it is type-checked and scanned for malware before it goes live,
        // and the current background keeps showing until then.
        setStatus('Checking the image…');
        const outcome = await waitForBackground(uploadedId);

        if (outcome === 'live') {
            renderBackgroundPreview();
            setStatus(`Background set (${Math.round(blob.size / 1024)}KB).`);
            showToast('Background updated', 'success');
        } else if (outcome === 'refused') {
            setStatus("That image couldn't be used. The previous background is unchanged.");
            showToast("That image couldn't be used as a background", 'error');
        } else if (outcome === 'waiting') {
            setStatus('Still being checked. It will appear on the guest page when it clears.');
        }
    } catch (err) {
        setStatus(err.message);
        showToast(err.message, 'error');
    }
}

/**
 * 'live' once the uploaded background is the one showing, 'refused' if checking finished and it
 * is not, 'waiting' if checking outlasts the poll, 'gone' if the organiser left the event.
 */
async function waitForBackground(uploadedId) {
    const eventId = state.event.id;

    for (let attempt = 0; attempt < BACKGROUND_POLL_LIMIT; attempt++) {
        await sleep(BACKGROUND_POLL_MS);
        if (state.event?.id !== eventId) return 'gone';

        let ev;
        try {
            ev = await api.getEvent(eventId);
        } catch {
            continue;
        }
        if (ev.background_id === uploadedId) {
            state.event = { ...state.event, ...ev };
            return 'live';
        }
        if (!ev.background_checking) {
            state.event = { ...state.event, ...ev };
            return 'refused';
        }
    }
    return 'waiting';
}

async function removeBackground() {
    if (!confirm('Remove the background image?')) return;
    try {
        await api.deleteEventBackground(state.event.id);
        state.event = { ...state.event, background_url: null, background_checking: false, background_id: null };
        renderBackgroundPreview();
        document.getElementById('background-status').textContent = '';
        showToast('Background removed', 'success');
    } catch (err) {
        showToast(err.message, 'error');
    }
}

function slugifyName(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'event';
}

function downloadDataUrl(dataUrl, filename) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    link.click();
}

/**
 * Draws the printable card on a canvas rather than opening a print window.
 *
 * A print window inherits none of the app's styling and is blocked by popup blockers on
 * exactly the phones an organiser is likely to be holding. A PNG can be printed, emailed,
 * or dropped into a document, and looks the same everywhere.
 */
async function downloadCard(url) {
    const ev = state.event;
    const canvas = document.createElement('canvas');
    canvas.width = 1000;
    canvas.height = 1400;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = '#0d9488';
    ctx.fillRect(0, 0, canvas.width, 16);

    const font = (size, weight = '400') =>
        `${weight} ${size}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;

    ctx.textAlign = 'center';
    ctx.fillStyle = '#15201d';
    ctx.font = font(58, '700');
    wrapText(ctx, ev.name, canvas.width / 2, 150, canvas.width - 140, 70);

    ctx.fillStyle = '#5c6b72';
    ctx.font = font(30);
    ctx.fillText(formatRange(new Date(ev.starts_at), new Date(ev.ends_at)), canvas.width / 2, 300);
    if (ev.location) ctx.fillText(ev.location, canvas.width / 2, 348);

    const qrDataUrl = await QRCode.toDataURL(url, { width: 560, margin: 1 });
    const qr = new Image();
    await new Promise((resolve, reject) => {
        qr.onload = resolve;
        qr.onerror = reject;
        qr.src = qrDataUrl;
    });
    ctx.drawImage(qr, (canvas.width - 560) / 2, 420, 560, 560);

    ctx.fillStyle = '#15201d';
    ctx.font = font(40, '600');
    ctx.fillText('Scan to share your photos', canvas.width / 2, 1075);

    ctx.fillStyle = '#5c6b72';
    ctx.font = font(26);
    ctx.fillText('Add pictures, video and a message to the guest book', canvas.width / 2, 1125);

    ctx.fillStyle = '#0d9488';
    ctx.font = font(28, '600');
    ctx.fillText(url.replace(/^https?:\/\//, ''), canvas.width / 2, 1200);

    downloadDataUrl(canvas.toDataURL('image/png'), `${slugifyName(ev.name)}-card.png`);
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = String(text).split(/\s+/);
    let line = '';
    let cursorY = y;
    for (const word of words) {
        const candidate = line ? `${line} ${word}` : word;
        if (ctx.measureText(candidate).width > maxWidth && line) {
            ctx.fillText(line, x, cursorY);
            line = word;
            cursorY += lineHeight;
        } else {
            line = candidate;
        }
    }
    if (line) ctx.fillText(line, x, cursorY);
}

// ------------------------------------------------------------------ Media

async function drawMedia(body, refreshes = 0) {
    clearTimeout(mediaRefreshTimer);
    if (refreshes === 0) body.innerHTML = '<p class="text-muted">Loading…</p>';

    let removed = [];
    try {
        ({ media: state.media, removed = [] } = await api.listEventMedia(state.event.id));
    } catch (err) {
        body.innerHTML = `<p class="form-error">${escapeHtml(err.message)}</p>`;
        return;
    }
    // The organiser may have switched tab while that request was out.
    if (state.tab !== 'media' || !body.isConnected) return;

    if (state.media.length === 0 && removed.length === 0) {
        body.innerHTML = `
            <div class="empty-state">
                <i class="fa-solid fa-images"></i>
                <h2>Nothing yet</h2>
                <p>Photos and video guests add will appear here.</p>
            </div>`;
        return;
    }

    body.innerHTML = `
        ${state.media.length > 0 ? `<div class="event-media-grid">${state.media.map(mediaTile).join('')}</div>` : ''}
        ${removedHtml(removed)}`;

    for (const btn of body.querySelectorAll('[data-view-media]')) {
        const item = state.media.find((m) => m.id === Number(btn.dataset.viewMedia));
        if (item) btn.addEventListener('click', () => openMediaViewer(item));
    }

    for (const btn of body.querySelectorAll('[data-delete-media]')) {
        btn.addEventListener('click', async () => {
            if (!confirm('Delete this permanently? It will also be removed from your gallery.')) return;
            try {
                await api.deleteEventMedia(state.event.id, Number(btn.dataset.deleteMedia));
                showToast('Deleted', 'success');
                drawMedia(body);
            } catch (err) {
                showToast(err.message, 'error');
            }
        });
    }

    if (state.media.some((m) => isChecking(m.status)) && refreshes < MEDIA_REFRESH_LIMIT) {
        mediaRefreshTimer = setTimeout(() => drawMedia(body, refreshes + 1), MEDIA_REFRESH_MS);
    }
}

function mediaTile(item) {
    const ready = item.status === 'ready';
    const badge = item.kind === 'video'
        ? '<span class="event-media-badge"><i class="fa-solid fa-play"></i></span>'
        : '';

    let media;
    if (ready) {
        const thumb = item.thumbnail_url
            ? `<img src="${escapeHtml(item.thumbnail_url)}" alt="" loading="lazy">`
            : `<div class="event-media-placeholder"><i class="fa-solid fa-${item.kind === 'video' ? 'film' : 'image'}"></i></div>`;
        media = `<button type="button" class="event-media-open" data-view-media="${item.id}"
                         aria-label="${item.kind === 'video' ? 'Play' : 'View'}">${thumb}${badge}</button>`;
    } else {
        // Nothing opens until the upload has passed its checks: there is no URL for it yet, and
        // a play badge would promise a clip the organiser cannot have.
        const checking = isChecking(item.status);
        const label = checking
            ? 'Checking upload…'
            : item.status === 'unchecked' ? "Couldn't be checked" : 'Processing failed';
        media = `
            <div class="event-media-placeholder event-media-state ${checking ? 'is-checking' : 'is-problem'}">
                <i class="fa-solid ${checking ? 'fa-shield-halved' : 'fa-triangle-exclamation'}"></i>
                <span>${label}</span>
            </div>`;
    }

    return `
        <figure class="event-media-tile${ready ? '' : ' is-pending'}">
            ${media}
            <figcaption>
                <span>${escapeHtml(item.guest_name ?? 'An organiser')}</span>
                <button type="button" class="event-media-delete" data-delete-media="${item.id}" aria-label="Delete">
                    <i class="fa-solid fa-trash"></i>
                </button>
            </figcaption>
        </figure>`;
}

/**
 * Uploads the malware scan caught. The files are gone -- deleted, every version, before anyone
 * saw them -- so this is only the record: who, what, and whether it got them blocked.
 */
function removedHtml(removed) {
    if (removed.length === 0) return '';
    return `
        <section class="event-removed">
            <h2 class="event-subheading"><i class="fa-solid fa-shield-halved"></i> Removed by the security check</h2>
            <p class="form-hint">
                These uploads contained malware, so they were deleted before anyone saw them.
                A guest is blocked automatically after ${STRIKES_BEFORE_BLOCK}.
            </p>
            <ul class="event-removed-list">
                ${removed.map((r) => `
                    <li>
                        <strong>${escapeHtml(r.uploader_name ?? 'Unknown uploader')}</strong>
                        <span>${escapeHtml(KIND_LABELS[r.kind] ?? r.kind)} &middot; ${escapeHtml(r.threat_name ?? 'Malware')}</span>
                        <time>${escapeHtml(new Date(r.detected_at).toLocaleString('en-GB'))}</time>
                        ${r.guest_blocked ? '<span class="event-badge event-badge-blocked">Guest blocked</span>' : ''}
                    </li>`).join('')}
            </ul>
        </section>`;
}

/**
 * The organiser's own view of a clip. Everything needed is already in the listing --
 * `video_url` is a presigned GET on the original -- so this is only ever a render.
 */
function openMediaViewer(item) {
    const poster = item.thumbnail_url ? ` poster="${escapeHtml(item.thumbnail_url)}"` : '';
    const body = item.kind === 'video'
        ? `<div class="event-media-viewer">
               <video controls autoplay playsinline preload="metadata"${poster}
                      src="${escapeHtml(item.video_url ?? '')}"></video>
           </div>`
        : `<div class="event-media-viewer">
               <img src="${escapeHtml(item.display_url ?? item.thumbnail_url ?? '')}" alt="">
           </div>`;

    openModal(item.guest_name ?? 'An organiser', body, { wide: true });
}

// ------------------------------------------------------------------ Guest book

async function drawGuestbook(body) {
    body.innerHTML = '<p class="text-muted">Loading…</p>';
    try {
        ({ entries: state.guestbook } = await api.listEventGuestbook(state.event.id));
    } catch (err) {
        body.innerHTML = `<p class="form-error">${escapeHtml(err.message)}</p>`;
        return;
    }

    if (state.guestbook.length === 0) {
        body.innerHTML = `
            <div class="empty-state">
                <i class="fa-solid fa-pen-nib"></i>
                <h2>The guest book is empty</h2>
                <p>Messages guests leave will appear here.</p>
            </div>`;
        return;
    }

    body.innerHTML = `<ul class="event-guestbook-list">${state.guestbook.map(guestbookRow).join('')}</ul>`;

    for (const btn of body.querySelectorAll('[data-delete-entry]')) {
        btn.addEventListener('click', async () => {
            if (!confirm('Delete this guest book entry?')) return;
            try {
                await api.deleteEventGuestbookEntry(state.event.id, Number(btn.dataset.deleteEntry));
                showToast('Deleted', 'success');
                drawGuestbook(body);
            } catch (err) {
                showToast(err.message, 'error');
            }
        });
    }
}

function guestbookRow(entry) {
    let content;
    if (entry.kind !== 'audio') {
        // escapeHtml, not innerHTML of the raw body: this is text a stranger typed into a
        // public form, and it is rendered on a page organisers read.
        content = `<p>${escapeHtml(entry.body ?? '')}</p>`;
    } else if (entry.status === 'ready') {
        content = `<audio controls preload="none" src="${escapeHtml(entry.audio_url ?? '')}"></audio>`;
    } else {
        const checking = isChecking(entry.status);
        content = `<p class="event-guestbook-state">
            <i class="fa-solid ${checking ? 'fa-shield-halved' : 'fa-triangle-exclamation'}"></i>
            ${checking ? 'Voice message being checked…' : "Voice message couldn't be checked"}
        </p>`;
    }

    return `
        <li class="event-guestbook-entry">
            <div class="event-guestbook-meta">
                <strong>${escapeHtml(entry.guest_name ?? 'An organiser')}</strong>
                <time>${escapeHtml(new Date(entry.created_at).toLocaleString('en-GB'))}</time>
            </div>
            ${content}
            <button type="button" class="btn btn-sm btn-danger" data-delete-entry="${entry.id}">Delete</button>
        </li>`;
}

// ------------------------------------------------------------------ Guests

async function drawGuests(body) {
    body.innerHTML = '<p class="text-muted">Loading…</p>';
    try {
        ({ guests: state.guests } = await api.listEventGuests(state.event.id));
    } catch (err) {
        body.innerHTML = `<p class="form-error">${escapeHtml(err.message)}</p>`;
        return;
    }

    if (state.guests.length === 0) {
        body.innerHTML = `
            <div class="empty-state">
                <i class="fa-solid fa-user-group"></i>
                <h2>Nobody has joined yet</h2>
                <p>Guests appear here once they've asked for a code.</p>
            </div>`;
        return;
    }

    body.innerHTML = `
        <div class="table-wrap">
            <table class="event-guest-table">
                <thead>
                    <tr><th>Name</th><th>Email</th><th>Confirmed</th><th>Added</th><th>Last seen from</th><th></th></tr>
                </thead>
                <tbody>${state.guests.map(guestRow).join('')}</tbody>
            </table>
        </div>`;

    for (const btn of body.querySelectorAll('[data-block]')) {
        btn.addEventListener('click', async () => {
            const blocked = btn.dataset.blocked === 'true';
            try {
                await api.blockEventGuest(state.event.id, Number(btn.dataset.block), !blocked);
                showToast(blocked ? 'Guest allowed back in' : 'Guest blocked', 'success');
                drawGuests(body);
            } catch (err) {
                showToast(err.message, 'error');
            }
        });
    }
}

function guestRow(guest) {
    // A strike is an upload the malware scan caught. It is shown here, next to Block, because
    // it is exactly what an organiser needs in front of them when deciding whether to use it.
    const strikes = guest.strikes > 0
        ? `<br><span class="event-strikes"><i class="fa-solid fa-shield-halved"></i>
               ${guest.strikes} of ${STRIKES_BEFORE_BLOCK} malware strikes</span>`
        : '';

    return `
        <tr class="${guest.blocked ? 'is-blocked' : ''}">
            <td>${escapeHtml(guest.name)}</td>
            <td>${escapeHtml(guest.email)}</td>
            <td>${guest.verified ? '<i class="fa-solid fa-check event-yes"></i>' : '<span class="text-faint">Not yet</span>'}</td>
            <td>${guest.media_count} photos, ${guest.guestbook_count} messages${strikes}</td>
            <td>${lastSeen(guest)}</td>
            <td>
                <button type="button" class="btn btn-sm ${guest.blocked ? 'btn-secondary' : 'btn-danger'}"
                        data-block="${guest.id}" data-blocked="${guest.blocked}">
                    ${guest.blocked ? 'Unblock' : 'Block'}
                </button>
            </td>
        </tr>`;
}

/**
 * The address the guest last used the event from -- the same address a security alert names,
 * which is what lets an organiser match one to a person. Empty until the guest has confirmed
 * their code, because a request for a code proves nothing about who sent it.
 */
function lastSeen(guest) {
    if (!guest.last_seen_ip) return '<span class="text-faint">Not yet</span>';
    const when = new Date(guest.last_seen_at).toLocaleString(undefined, {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
    return `<span class="event-guest-ip">${escapeHtml(guest.last_seen_ip)}</span>
            <br><span class="text-faint">${escapeHtml(when)}</span>`;
}

// ------------------------------------------------------------------ Edit / admins / delete

function openEditModal() {
    const ev = state.event;
    const { close, body } = openModal('Edit event', `
        <form id="edit-event-form" class="event-form">
            <div class="form-group">
                <label for="ed-name">Name</label>
                <input type="text" id="ed-name" maxlength="200" required value="${escapeHtml(ev.name)}">
            </div>
            <div class="form-group">
                <label for="ed-location">Where</label>
                <input type="text" id="ed-location" maxlength="200" value="${escapeHtml(ev.location ?? '')}">
            </div>
            <div class="form-group">
                <label for="ed-description">Welcome message</label>
                <textarea id="ed-description" rows="3" maxlength="2000">${escapeHtml(ev.description ?? '')}</textarea>
            </div>
            <div class="event-form-row">
                <div class="form-group">
                    <label for="ed-starts">Starts</label>
                    <input type="datetime-local" id="ed-starts" required value="${toLocalInputValue(new Date(ev.starts_at))}">
                </div>
                <div class="form-group">
                    <label for="ed-ends">Ends</label>
                    <input type="datetime-local" id="ed-ends" required value="${toLocalInputValue(new Date(ev.ends_at))}">
                </div>
            </div>
            <div class="form-group setting-row">
                <label>
                    <input type="checkbox" id="ed-uploads" ${ev.allow_uploads ? 'checked' : ''}>
                    <strong>Let guests add photos and video</strong>
                </label>
                <p class="form-hint">Turn this off to close uploads without ending the event.</p>
            </div>
            <div class="form-group setting-row">
                <label>
                    <input type="checkbox" id="ed-guestbook" ${ev.allow_guestbook ? 'checked' : ''}>
                    <strong>Let guests sign the guest book</strong>
                </label>
                <p class="form-hint">Written messages and short voice recordings.</p>
            </div>
            <div id="edit-error" class="form-error hidden"></div>
            <div class="modal-actions">
                <button type="button" class="btn btn-secondary" id="cancel-edit">Cancel</button>
                <button type="submit" class="btn btn-primary">Save</button>
            </div>
        </form>`);

    body.querySelector('#cancel-edit').addEventListener('click', close);
    body.querySelector('#edit-event-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const error = body.querySelector('#edit-error');
        error.classList.add('hidden');
        try {
            state.event = {
                ...state.event,
                ...(await api.updateEvent(ev.id, {
                    name: body.querySelector('#ed-name').value,
                    location: body.querySelector('#ed-location').value,
                    description: body.querySelector('#ed-description').value,
                    starts_at: new Date(body.querySelector('#ed-starts').value).toISOString(),
                    ends_at: new Date(body.querySelector('#ed-ends').value).toISOString(),
                    allow_uploads: body.querySelector('#ed-uploads').checked,
                    allow_guestbook: body.querySelector('#ed-guestbook').checked,
                })),
            };
            close();
            showToast('Saved', 'success');
            await refreshEventMenu();
            draw();
        } catch (err) {
            error.textContent = err.message;
            error.classList.remove('hidden');
        }
    });
}

async function openAdminsModal() {
    let users = [];
    try {
        ({ users } = await api.listShareTargets());
    } catch (err) {
        showToast(err.message, 'error');
        return;
    }

    if (users.length === 0) {
        openModal('Joint admins', '<p>There is nobody else in the portal to add yet.</p>');
        return;
    }

    const current = new Set(state.event.admin_ids);
    const { close, body } = openModal('Joint admins', `
        <p class="form-hint">
            Joint admins can moderate photos and guest-book entries, and every photo guests
            upload is shared into their gallery too. Only you can delete the event.
        </p>
        <div class="share-people-list">
            ${users.map((u) => `
                <label class="share-person">
                    <input type="checkbox" value="${u.id}" ${current.has(u.id) ? 'checked' : ''}>
                    <span>${escapeHtml(u.full_name)}</span>
                </label>`).join('')}
        </div>
        <div id="admins-error" class="form-error hidden"></div>
        <div class="modal-actions">
            <button type="button" class="btn btn-secondary" id="cancel-admins">Cancel</button>
            <button type="button" class="btn btn-primary" id="save-admins">Save</button>
        </div>`);

    body.querySelector('#cancel-admins').addEventListener('click', close);
    body.querySelector('#save-admins').addEventListener('click', async () => {
        const ids = [...body.querySelectorAll('input[type="checkbox"]:checked')].map((el) => Number(el.value));
        const error = body.querySelector('#admins-error');
        error.classList.add('hidden');
        try {
            const { admin_ids } = await api.setEventAdmins(state.event.id, ids);
            state.event.admin_ids = admin_ids;
            close();
            showToast('Admins updated', 'success');
            draw();
        } catch (err) {
            error.textContent = err.message;
            error.classList.remove('hidden');
        }
    });
}

async function confirmDelete() {
    if (!confirm(
        'Delete this event?\n\nGuests lose access immediately and the guest book is removed. '
        + 'Photos guests uploaded stay in your gallery.',
    )) return;

    try {
        await api.deleteEvent(state.event.id);
        showToast('Event deleted', 'success');
        await refreshEventMenu();
        router.navigate('/events');
    } catch (err) {
        showToast(err.message, 'error');
    }
}
